
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Eye, EyeOff, Calendar, Check } from 'lucide-react';
import Toast from '../components/Toast';

const AuthPage = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        confirmPassword: '',
        birthDate: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [toast, setToast] = useState(null);

    const { login, register } = useAuth();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Hata mesajını temizle
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'E-posta gerekli';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Geçerli bir e-posta adresi girin';
        }

        if (!formData.password) {
            newErrors.password = 'Şifre gerekli';
        } else if (!isLogin) {
            // Sadece kayıt olurken güçlü şifre kontrolü yap
            if (formData.password.length < 6) {
                newErrors.password = 'Şifre en az 6 karakter olmalı';
            } else if (!/[A-Z]/.test(formData.password)) {
                newErrors.password = 'Şifre en az 1 büyük harf içermelidir';
            } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
                newErrors.password = 'Şifre en az 1 özel karakter içermelidir (!@#$%^&* vb.)';
            }
        }

        if (!isLogin) {
            if (!formData.name) {
                newErrors.name = 'İsim gerekli';
            }
            if (!formData.birthDate) {
                newErrors.birthDate = 'Doğum tarihi gerekli';
            } else {
                // 18 yaş kontrolü
                const birthDate = new Date(formData.birthDate);
                const today = new Date();
                const age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    if (age - 1 < 18) {
                        newErrors.birthDate = '18 yaşından büyük olmalısınız';
                    }
                } else if (age < 18) {
                    newErrors.birthDate = '18 yaşından büyük olmalısınız';
                }
            }
            if (!formData.confirmPassword) {
                newErrors.confirmPassword = 'Şifre onayı gerekli';
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Şifreler eşleşmiyor';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('🔐 Giriş denemesi başlatılıyor...', {
            email: formData.email,
            isLogin,
            formData: { ...formData, password: '***' }
        });

        if (!validateForm()) {
            console.log('❌ Form validasyon hatası:', errors);
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            // Email'i normalize et (trim ve lowercase)
            const normalizedEmail = formData.email.trim().toLowerCase();

            if (isLogin) {
                await login(normalizedEmail, formData.password);
                // Başarılı giriş - tik göster ve yönlendir
                setShowSuccess(true);
                setTimeout(() => {
                    // Otomatik yönlendirme için sayfayı yenile (veya navigate kullan)
                    window.location.href = '/';
                }, 1500);
            } else {
                const result = await register(normalizedEmail, formData.password, formData.name, formData.birthDate);

                // E-posta doğrulama gerekiyorsa bilgi göster
                if (result?.needsEmailVerification) {
                    // Form'u temizle ve login moduna geç
                    setFormData({
                        email: '',
                        password: '',
                        name: '',
                        confirmPassword: '',
                        birthDate: ''
                    });
                    setErrors({});
                    setIsLogin(true); // Login moduna geç

                    setToast({
                        message: result.message,
                        type: 'info'
                    });
                } else {
                    // Başarılı kayıt - form'u temizle ve login moduna geç
                    setFormData({
                        email: '',
                        password: '',
                        name: '',
                        confirmPassword: '',
                        birthDate: ''
                    });
                    setErrors({});
                    setIsLogin(true); // Login moduna geç

                    setShowSuccess(true);
                    setTimeout(() => {
                        window.location.href = '/';
                    }, 1500);
                }
            }
        } catch (error) {
            // Hata mesajını toast ile göster
            const errorMessage = error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
            setToast({
                message: errorMessage,
                type: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAuthMode = () => {
        setIsLogin(!isLogin);
        setFormData({
            email: '',
            password: '',
            name: '',
            confirmPassword: '',
            birthDate: ''
        });
        setErrors({});
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="flex justify-center items-center mb-4">
                        <img
                            src="/logo.png"
                            alt="Removvie Logo"
                            className="h-16 w-auto"
                        />
                    </div>
                    <h2 className="text-xl text-gray-600 dark:text-gray-300">
                        {isLogin ? 'Hesabınıza Giriş Yapın' : 'Yeni Hesap Oluşturun'}
                    </h2>
                </div>

                <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-xl rounded-lg">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {errors.general && (
                            <div
                                data-error-message
                                className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2 duration-300"
                            >
                                <div className="flex items-center space-x-2">
                                    <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <p className="font-medium">{errors.general}</p>
                                </div>
                            </div>
                        )}

                        {!isLogin && (
                            <div>
                                <label htmlFor="name" className="sr-only">İsim</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border ${errors.name ? 'border-red-300' : 'border-gray-300'
                                            } dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10`}
                                        placeholder="İsim"
                                    />
                                </div>
                                {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
                            </div>
                        )}

                        {!isLogin && (
                            <div>
                                <label htmlFor="birthDate" className="sr-only">Doğum Tarihi</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="birthDate"
                                        name="birthDate"
                                        type="date"
                                        value={formData.birthDate}
                                        onChange={handleInputChange}
                                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                        className={`appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border ${errors.birthDate ? 'border-red-300' : 'border-gray-300'
                                            } dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10`}
                                    />
                                </div>
                                {errors.birthDate && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.birthDate}</p>}
                            </div>
                        )}

                        <div>
                            <label htmlFor="email" className="sr-only">E-posta</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border ${errors.email ? 'border-red-300' : 'border-gray-300'
                                        } dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10`}
                                    placeholder="E-posta adresi"
                                />
                            </div>
                            {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
                        </div>

                        <div>
                            <label htmlFor="password" className="sr-only">Şifre</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={!isLogin ? 'password' : (showPassword ? 'text' : 'password')}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`appearance-none rounded-md relative block w-full px-3 py-2 ${!isLogin ? 'pl-10' : 'pl-10 pr-10'} border ${errors.password ? 'border-red-300' : 'border-gray-300'
                                        } dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10`}
                                    placeholder="Şifre"
                                />
                                {isLogin && (
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                        >
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>}
                        </div>

                        {!isLogin && (
                            <div>
                                <label htmlFor="confirmPassword" className="sr-only">Şifre Onayı</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        className={`appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                                            } dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10`}
                                        placeholder="Şifre Onayı"
                                    />
                                </div>
                                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>}
                            </div>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading || showSuccess}
                                className="group relative w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {showSuccess ? (
                                    <>
                                        <Check className="h-5 w-5 mr-2" />
                                        {isLogin ? 'Giriş Başarılı!' : 'Kayıt Başarılı!'}
                                    </>
                                ) : isLoading ? (
                                    'İşleniyor...'
                                ) : (
                                    isLogin ? 'Giriş Yap' : 'Kayıt Ol'
                                )}
                            </button>
                        </div>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={toggleAuthMode}
                                className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                            >
                                {isLogin ? 'Hesabınız yok mu? Kayıt olun' : 'Zaten hesabınız var mı? Giriş yapın'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AuthPage; 